import { useAnalytics } from '@contexts/AnalyticsContext'
import {
  FetchVenueAnalyticsGraphParams,
  VenueAnalyticsGlobalVenuesOpening,
} from '@models/venue/VenueAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import { tuttiFruttiPalette } from '@utils/colors'
import { useEffect, useState } from 'react'
import { Bar, Cell, ComposedChart, LabelList, Tooltip, YAxis } from 'recharts'
import { NumberFormatedAxisTick } from '../common/AxisTicks'
import BaseChart from '../common/BaseChart'
import CustomTooltip from '../common/CustomTooltip'
import { useBarColors } from '../common/useBarColors'

interface Props {
  //
}

const GlobalVenuesOpening = ({}: Props) => {
  const { selectedDates } = useAnalytics()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>(undefined)
  const [chartData, setChartData] = useState<VenueAnalyticsGlobalVenuesOpening>(
    []
  )
  const barColors = useBarColors(chartData, 'count', tuttiFruttiPalette, true)

  const fetchData = async () => {
    setIsLoading(true)
    setError(undefined)

    if (!selectedDates) {
      setChartData([])
      setError('No data available.')
      setIsLoading(false)
      return
    }

    const fetchParams: FetchVenueAnalyticsGraphParams = {
      entity: 'venue',
      kind: 'global-venues-opening-history',
      startDate: selectedDates[0],
      endDate: selectedDates[1],
    }

    try {
      const response =
        await fetchAnalyticsGraph<VenueAnalyticsGlobalVenuesOpening>(
          fetchParams
        )
      const data = response.data
      setChartData(data || [])

      if (!data) {
        setError('No data available.')
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      setError('An error occurred while fetching data.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [selectedDates])
  const renderCustomizedLabel = (props: any) => {
    const { x, y, width, height, value } = props
    return value ? (
      <text
        x={x + width / 2}
        y={y + height / 2}
        fill="#fff"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={12}
        fontWeight={500}
        transform={`rotate(-90, ${x + width / 2}, ${y + height / 2})`}
      >
        {value == 'Venue #$$_posthog_breakdown_other_$$' ? 'Others' : value}
      </text>
    ) : null
  }
  return (
    <BaseChart
      title="Top 30 venues by views"
      help="Top 30 venues ranked by the number of views (page opening) over the selected time range."
      data={chartData}
      isLoading={isLoading}
      error={error}
      height={350}
    >
      <ComposedChart
        width={500}
        height={400}
        data={chartData}
        margin={{
          top: 10,
          right: 30,
          left: 0,
          bottom: 0,
        }}
      >
        <YAxis
          dataKey="count"
          tickLine={false}
          stroke={'#bbb'}
          width={35}
          tick={(props) => <NumberFormatedAxisTick {...props} />}
        />
        <Tooltip
          content={(props) => (
            <CustomTooltip
              type="bar"
              {...props}
              valueFormatter={(val) => val + '  views'}
              customLabelFormatter={(payload) => {
                return payload.venueName ==
                  'Venue #$$_posthog_breakdown_other_$$'
                  ? 'Others'
                  : payload.venueName
              }}
            />
          )}
        />
        <Bar dataKey="count">
          {barColors.map((color, index) => (
            <Cell key={index} fill={color} />
          ))}
          <LabelList
            dataKey="venueName"
            position="top"
            content={renderCustomizedLabel}
          />
        </Bar>
      </ComposedChart>
    </BaseChart>
  )
}

export default GlobalVenuesOpening
